import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, UserPlus, Users } from "lucide-react";

import {
  getStandaloneSchemeById,
  updateStandaloneScheme,
  createStandaloneAssignment,
  getAllStandaloneAssignments,
  getTeachersForStandalone,
} from "../../api/markschedule/standaloneMarksApi";
import {
  getStandaloneSchemeByIdPortal,
  updateStandaloneSchemePortal,
  createStandaloneAssignmentPortal,
  getAllStandaloneAssignmentsPortal,
  getTeachersForStandalonePortal,
} from "../../api/markschedule/standaloneMarksPortalApi";
import {
  getAllClasses,
  getAllSections,
} from "../../api/student/studentApi";
import {
  getAllClassesForPortal,
  getAllSectionsForPortal,
} from "../../api/student/studentPortalApi";
import { useStaffAuth } from "../../context/StaffAuthContext";
import AssignmentStatusBadge from "../../components/markschedule/AssignmentStatusBadge";
import {
  STANDALONE_SCHEME_TYPE_LABELS,
} from "../../utils/markschedule/marksConstants";
import {
  pickList,
  toId,
  teacherLabel,
} from "../../utils/markschedule/marksHelpers";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  Badge,
  Alert,
  EmptyState,
  Skeleton,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const StandaloneSchemeDetail = ({ isPortal = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canManage = isPortal
    ? staffAuth?.hasPermission?.("EXAM_MANAGEMENT_FULL")
    : true;

  const [scheme, setScheme] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [meta, setMeta] = useState({ name: "", description: "", maxMarks: "" });
  const [assignForm, setAssignForm] = useState({
    classId: "",
    sectionId: "",
    assignedStaffId: "",
    deadline: "",
  });

  const toast = useToast();

  const listPath = isPortal
    ? "/staff-panel/marks/standalone"
    : "/marks/standalone";

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [schRes, asgRes, classRes] = await Promise.all([
        isPortal
          ? getStandaloneSchemeByIdPortal(id)
          : getStandaloneSchemeById(id),
        isPortal
          ? getAllStandaloneAssignmentsPortal({ schemeId: id })
          : getAllStandaloneAssignments({ schemeId: id }),
        isPortal ? getAllClassesForPortal() : getAllClasses(),
      ]);
      const sch = schRes.scheme || schRes.data || schRes;
      setScheme(sch);
      setMeta({
        name: sch.name || "",
        description: sch.description || "",
        maxMarks: sch.maxMarks ?? "",
      });
      setAssignments(pickList(asgRes, ["assignments", "data"]));
      setClasses(pickList(classRes, ["classes", "data"]));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id, isPortal]);

  useEffect(() => {
    load();
  }, [load]);

  const onClassChange = async (classId) => {
    setAssignForm((f) => ({
      ...f,
      classId,
      sectionId: "",
      assignedStaffId: "",
    }));
    if (!classId) {
      setSections([]);
      setTeachers([]);
      return;
    }
    try {
      const [secRes, teachRes] = await Promise.all([
        isPortal
          ? getAllSectionsForPortal(classId)
          : getAllSections(classId),
        isPortal
          ? getTeachersForStandalonePortal({
              classId,
              subjectId: toId(scheme?.subjectId) || undefined,
            })
          : getTeachersForStandalone({
              classId,
              subjectId: toId(scheme?.subjectId) || undefined,
            }),
      ]);
      setSections(pickList(secRes, ["sections", "data"]));
      setTeachers(pickList(teachRes, ["teachers", "data"]));
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveMeta = async () => {
    try {
      setSubmitting(true);
      const payload = {
        name: meta.name,
        description: meta.description,
        maxMarks: Number(meta.maxMarks),
      };
      if (isPortal) await updateStandaloneSchemePortal(id, payload);
      else await updateStandaloneScheme(id, payload);
      toast.success("Scheme updated");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (
      !assignForm.classId ||
      !assignForm.sectionId ||
      !assignForm.assignedStaffId
    ) {
      toast.error("Class, section and teacher are required");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        schemeId: id,
        classId: assignForm.classId,
        sectionId: assignForm.sectionId,
        assignedStaffId: assignForm.assignedStaffId,
        deadline: assignForm.deadline || null,
      };
      if (isPortal) await createStandaloneAssignmentPortal(payload);
      else await createStandaloneAssignment(payload);
      toast.success("Assignment created");
      setAssignForm({
        classId: "",
        sectionId: "",
        assignedStaffId: "",
        deadline: "",
      });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Assign failed");
    } finally {
      setSubmitting(false);
    }
  };

  const classOptions = useMemo(
    () => [
      { label: "Class *", value: "" },
      ...classes.map((c) => ({
        label: c.className || c.name,
        value: toId(c),
      })),
    ],
    [classes]
  );

  const sectionOptions = useMemo(
    () => [
      { label: "Section *", value: "" },
      ...sections.map((s) => ({
        label: s.sectionName || s.name,
        value: toId(s),
      })),
    ],
    [sections]
  );

  const teacherOptions = useMemo(
    () => [
      { label: "Teacher *", value: "" },
      ...teachers.map((t) => ({
        label: teacherLabel(t),
        value: toId(t.staff || t),
      })),
    ],
    [teachers]
  );

  const columns = [
    {
      key: "classSection",
      header: "Class · Section",
      render: (a) => (
        <span className="font-medium text-neutral-900 dark:text-neutral-100">
          {a.classId?.className} · {a.sectionId?.sectionName}
        </span>
      ),
    },
    {
      key: "teacher",
      header: "Assigned Teacher",
      render: (a) => (
        <span className="text-neutral-600 dark:text-neutral-300">{a.assignedStaffId?.name || "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (a) => <AssignmentStatusBadge status={a.status} />,
    },
  ];

  const tableRows = useMemo(
    () => assignments.map((a) => ({ ...a, id: toId(a) })),
    [assignments]
  );

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 p-6">
        <Skeleton height={24} width="100px" />
        <Skeleton height={140} width="100%" />
        <Skeleton height={220} width="100%" />
      </div>
    );
  }

  if (!scheme) {
    return (
      <div className="p-8 text-center">
        <Alert variant="danger">Scheme not found</Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      {/* Back Button */}
      <button
        type="button"
        onClick={() => navigate(listPath)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Schemes
      </button>

      {/* Alert Notifications */}
      
      

      {/* Scheme Metadata Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
              {scheme.name}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {STANDALONE_SCHEME_TYPE_LABELS[scheme.type] || scheme.type}
              </Badge>
              <Badge variant="outline">{scheme.academicYear}</Badge>
            </div>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            Max Marks: <span className="font-medium text-neutral-700 dark:text-neutral-200">{scheme.maxMarks}</span>
          </p>
        </CardHeader>
        {canManage && (
          <CardContent className="space-y-4 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <Input
                label="Scheme Name"
                value={meta.name}
                onChange={(e) => setMeta({ ...meta, name: e.target.value })}
              />
              <Input
                type="number"
                min={1}
                label="Max Marks"
                value={meta.maxMarks}
                onChange={(e) => setMeta({ ...meta, maxMarks: e.target.value })}
              />
              <div className="md:col-span-2">
                <Input
                  label="Description"
                  value={meta.description}
                  onChange={(e) =>
                    setMeta({ ...meta, description: e.target.value })
                  }
                  placeholder="Optional description"
                />
              </div>
            </div>
            <div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                isLoading={submitting}
                onClick={handleSaveMeta}
                leftIcon={<Save className="h-4 w-4" />}
              >
                Save Details
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Assign Teacher Form */}
      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-primary-900 dark:text-primary-300" /> Assign to Teacher
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAssign} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Select
                  options={classOptions}
                  value={assignForm.classId}
                  onChange={(val) => onClassChange(val)}
                  required
                />
                <Select
                  options={sectionOptions}
                  value={assignForm.sectionId}
                  onChange={(val) =>
                    setAssignForm({ ...assignForm, sectionId: val })
                  }
                  required
                />
                <Select
                  options={teacherOptions}
                  value={assignForm.assignedStaffId}
                  onChange={(val) =>
                    setAssignForm({
                      ...assignForm,
                      assignedStaffId: val,
                    })
                  }
                  required
                />
                <Input
                  type="date"
                  label="Deadline (optional)"
                  value={assignForm.deadline}
                  onChange={(e) =>
                    setAssignForm({ ...assignForm, deadline: e.target.value })
                  }
                />
              </div>
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={submitting}
                >
                  Assign
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Assignments Table Card */}
      <Card>
        <CardHeader className="pb-3 border-b border-neutral-200 dark:border-neutral-800">
          <CardTitle className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
            Assignments ({assignments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            emptyState={
              <EmptyState
                icon={Users}
                title="No assignments created"
                description="Assign this scheme to a class, section, and teacher above."
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default StandaloneSchemeDetail;